import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useNetwork } from '@/contexts/NetworkContext';
import { changeDomainNetwork } from '@/services/api/domains';
import type { Domain } from '@/types/domain';

interface Props {
    domain: Domain | null;
    onClose: () => void;
}

// "Trocar conta GAM" de um domínio. Só admin chega aqui (a API também confere).
export default function TrocarContaGamDialog({ domain, onClose }: Props) {
    const { networks, format, setSelected } = useNetwork();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [destino, setDestino] = useState<string>('');

    const opcoes = networks.filter((n) => n.network_code !== domain?.network);

    useEffect(() => {
        setDestino(opcoes[0]?.network_code ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain?.id]);

    const mutation = useMutation({
        mutationFn: () => changeDomainNetwork(domain!.id, destino), // só abre com domínio
        onSuccess: (r) => {
            toast({
                title: 'Conta GAM trocada',
                description:
                    `${r.domain.domain} agora está em ${format(r.to_network)}. ` +
                    (r.detached_blocks
                        ? `${r.detached_blocks} bloco(s) arquivado(s) da conta antiga foram desvinculados. `
                        : '') +
                    'Próximo passo: Blocks › Gerar blocos.',
            });
            queryClient.invalidateQueries({ queryKey: ['domains'] });
            // O domínio saiu da conta selecionada: leva o painel para a conta dele.
            setSelected(r.to_network);
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Não foi possível trocar a conta',
                description: error.response?.data?.detail || 'Erro ao trocar a conta GAM.',
            });
        },
    });

    return (
        <Dialog open={!!domain} onOpenChange={(aberto) => !aberto && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Trocar conta GAM</DialogTitle>
                    <DialogDescription>{domain?.domain}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div className="space-y-1">
                        <Label>Conta atual</Label>
                        <p className="text-sm text-gray-900">{format(domain?.network)}</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="conta-destino">Nova conta</Label>
                        <Select value={destino || undefined} onValueChange={setDestino}>
                            <SelectTrigger id="conta-destino">
                                <SelectValue placeholder="Selecione a conta" />
                            </SelectTrigger>
                            <SelectContent>
                                {opcoes.map((n) => (
                                    <SelectItem key={n.network_code} value={n.network_code}>
                                        {n.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
                        <div className="space-y-1">
                            <p>Só funciona se o domínio <strong>não tiver bloco ativo</strong> — site servindo anúncio não troca de conta.</p>
                            <p>Os blocos arquivados da conta atual são desvinculados do domínio (ficam no histórico e continuam no GAM antigo). Depois, gere os blocos de novo em <strong>Blocks</strong> e o script em <strong>Setup</strong>.</p>
                            <p>O <code>ad.js</code> publicado não muda até você gerar e publicar o novo.</p>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button variant="outline" className="flex-1" onClick={onClose} disabled={mutation.isPending}>
                            Cancelar
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={() => mutation.mutate()}
                            disabled={!destino || mutation.isPending}
                        >
                            {mutation.isPending ? 'Trocando...' : 'Trocar conta'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
